import React, { useState, useEffect, memo } from 'react';
import { graphql, useFragment } from 'react-relay';
import { Box, xcss } from '@atlaskit/primitives';
import { ff } from '@atlassian/jira-feature-flagging';
import {
	AsyncIssueStatusField,
	IssueStatusFieldReadonly,
	type StatusValue,
} from '@atlassian/jira-issue-field-status/async';
import type { issueStatus_nativeIssueTable_IssueStatusCell$key } from '@atlassian/jira-relay/src/__generated__/issueStatus_nativeIssueTable_IssueStatusCell.graphql';
import type { issueStatus_nativeIssueTable_IssueStatusCell_isEditableInIssueView$key } from '@atlassian/jira-relay/src/__generated__/issueStatus_nativeIssueTable_IssueStatusCell_isEditableInIssueView.graphql';
import { toIssueKey, toIssueId } from '@atlassian/jira-shared-types';
import ErrorCell from '../../error-cell';
import { toStatusValue, toJiraStatusField, type JiraStatusField } from './utils';

export type Props = {
	fieldRef: issueStatus_nativeIssueTable_IssueStatusCell$key;
	// TODO Remove when cleaning up 'jsv-one-issue-table-spreadsheet-ui_ygrnf'
	hasInlineEditing: boolean;
	issueKey: string;
	issueId?: string;
	onUpdateCache?: (issueKey: string, fieldName: string, field: JiraStatusField) => void;
};

export const IssueStatusCell = memo<Props>(
	({ fieldRef, hasInlineEditing, issueKey, onUpdateCache, issueId }) => {
		const data = useFragment<issueStatus_nativeIssueTable_IssueStatusCell$key>(
			/* eslint-disable jira/relay/graphql-naming */
			graphql`
				fragment issueStatus_nativeIssueTable_IssueStatusCell on JiraStatusField
				@argumentDefinitions(
					isSpreadsheetUIEnabled: {
						type: "Boolean!"
						provider: "@atlassian/jira-relay-provider/src/status-inline-edit.relayprovider"
					}
					isInlineEditingEnabled: { type: "Boolean!" }
				) {
					status {
						name
						statusId
						statusCategory {
							statusCategoryId
						}
					}
					...issueStatus_nativeIssueTable_IssueStatusCell_isEditableInIssueView
						@arguments(isInlineEditingEnabled: $isInlineEditingEnabled)
						@include(if: $isSpreadsheetUIEnabled)
				}
			`,
			/* eslint-enable jira/relay/graphql-naming */
			fieldRef,
		);

		// We need an intermediary fragment as we have 2 include conditions pre-ff rollout: 1. FF; 2. Consumer query
		// variable. Once FF is cleaned up this can be inlined into the above fragment.
		const inlineEditData =
			useFragment<issueStatus_nativeIssueTable_IssueStatusCell_isEditableInIssueView$key>(
				graphql`
					fragment issueStatus_nativeIssueTable_IssueStatusCell_isEditableInIssueView on JiraStatusField
					@argumentDefinitions(isInlineEditingEnabled: { type: "Boolean!" }) {
						id @include(if: $isInlineEditingEnabled)
					}
				`,
				// Data will always be defined even when our fragment has been conditionally skipped. We need an additional
				// FF check to make sure we're not attempting to look up a non-existent fragment which leads to an invariant
				// violation.
				ff('jsv-one-issue-table-spreadsheet-ui_ygrnf') ? data : null,
			);

		const { status } = data;
		const [value, setValue] = useState<JiraStatusField>(status);
		useEffect(() => {
			setValue(status);
		}, [status]);

		if (
			value?.statusCategory?.statusCategoryId === undefined ||
			!value?.name?.length ||
			!value?.statusId?.length
		) {
			return <ErrorCell cellType="status" reason="Missing required fields" />;
		}

		const onSuccess = (newStatus: StatusValue) => {
			const transformedStatus = toJiraStatusField(newStatus);
			setValue(transformedStatus);
			onUpdateCache && onUpdateCache(issueKey, 'status', transformedStatus);
		};

		let isEditable;
		if (ff('jsv-one-issue-table-spreadsheet-ui_ygrnf')) {
			// If the property does not exist in fragment data then we know it has been conditionally excluded because
			// inline editing is disabled. In future this should follow the same design/deferred loading strategy of
			// other inline editable fields, however this would require a larger effort to decompose the status field.
			isEditable = inlineEditData && typeof inlineEditData.id === 'string';
		} else {
			isEditable = hasInlineEditing;
		}

		return (
			<Box xcss={alignmentFixStyles}>
				{isEditable ? (
					<AsyncIssueStatusField
						issueKey={toIssueKey(issueKey)}
						issueId={issueId && toIssueId(issueId)}
						appearance="lozenge"
						value={toStatusValue(value)}
						onSuccess={onSuccess}
					/>
				) : (
					<IssueStatusFieldReadonly value={toStatusValue(value)} />
				)}
			</Box>
		);
	},
);

// Status lozenges have a 2px invisible border around them to keep them in the same place if they enter an "error" state (where a border colour is added).
// Refer to: src/packages/issue/fields/status/src/common/ui/status-lozenge/main.tsx

const alignmentFixStyles = xcss({
	marginLeft: 'space.negative.025',
});
